import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Tooltip } from '@diwata/ironvale'
import { useUI } from '@/store/ui'
import { SigilsAPI, type Sigil } from '@/api/sigils'
import { homeStripSigils, sigilHint, sigilState, type SigilState } from '@/lib/sigils'
import './sigils.css'

// Sigils — the suite-wide marks of progression. Earned marks glow with the accent; locked
// ones sit dimmed under a "how to earn" hover; dormant cross-app ones read "coming with
// Sanctum/Eden". Every glyph is a single character, so the whole system is theme-safe and
// ships no image. The strip lives in Home's progression corner; the card is the gallery unit.

function stateLabel(state: SigilState, s: Sigil): string {
  if (state === 'earned') return s.earned_at ? `earned ${s.earned_at.slice(0, 10)}` : 'earned'
  if (state === 'coming') return 'coming soon'
  return 'not yet'
}

/** The mark itself — one glyph in a bordered square, tinted by state. */
export function SigilMark({ sigil, size = 'md' }: { sigil: Sigil; size?: 'sm' | 'md' | 'lg' }) {
  const state = sigilState(sigil)
  return (
    <span className={`dw-sigil-mark dw-sigil-mark--${state} dw-sigil-mark--${size}`} aria-hidden="true">
      {sigil.glyph}
    </span>
  )
}

/** Compact strip pill: mark + name, the whole thing a hover-help trigger into the gallery. */
function SigilBadge({ sigil }: { sigil: Sigil }) {
  const navigate = useNavigate()
  const state = sigilState(sigil)
  return (
    <Tooltip label={sigilHint(sigil)}>
      <button
        type="button"
        className={`dw-sigil-badge dw-sigil-badge--${state}`}
        onClick={() => navigate('/sigils')}
      >
        <SigilMark sigil={sigil} size="sm" />
        <span className="dw-sigil-badge-name">{sigil.name}</span>
      </button>
    </Tooltip>
  )
}

/**
 * Home's earned-sigils strip (inside the progression corner). Earned marks first and bright,
 * then a few in-reach ones dimmed as the visible "next to earn". Dormant cross-app marks get a
 * single quiet line pointing at the gallery — the full roster lives at /sigils.
 */
export function SigilsStrip() {
  const { diwaUrl } = useUI()
  const { data, isLoading, isError } = useQuery({
    queryKey: ['sigils'],
    queryFn: () => new SigilsAPI(diwaUrl).listSigils(),
  })
  const sigils = data?.sigils ?? []
  const strip = homeStripSigils(sigils)
  const comingCount = sigils.filter((s) => !s.earned && s.cross_app).length

  if (isLoading) return <div className="dw-rail-empty">Reading your sigils…</div>
  if (isError) return <div className="dw-rail-empty">Couldn't load your sigils — it will retry.</div>
  if (sigils.length === 0) return null

  return (
    <div className="dw-sigils-strip">
      <div className="dw-sigils-strip-row">
        {strip.map((s) => (
          <SigilBadge key={s.key} sigil={s} />
        ))}
      </div>
      {comingCount > 0 && (
        <div className="dw-sigils-strip-coming">
          + {comingCount} more coming with Sanctum &amp; Eden
        </div>
      )}
    </div>
  )
}

/** One gallery card: mark, name, gloss, the how-to-earn / status line, and earned_at. */
export function SigilCard({ sigil }: { sigil: Sigil }) {
  const state = sigilState(sigil)
  return (
    <div className={`iv-card iv-card--base dw-sigil-card dw-sigil-card--${state}`}>
      <div className="dw-sigil-card-head">
        <SigilMark sigil={sigil} size="lg" />
        <div className="dw-sigil-card-title">
          <span className="dw-sigil-card-name">{sigil.name}</span>
          <span className={`dw-sigil-card-status dw-sigil-card-status--${state}`}>
            {stateLabel(state, sigil)}
          </span>
        </div>
      </div>
      <p className="dw-sigil-card-gloss">{sigil.gloss}</p>
      {state !== 'earned' && (
        <p className="dw-sigil-card-how">{sigilHint(sigil)}</p>
      )}
      {sigil.perk && (
        <p
          className={`dw-sigil-perk dw-sigil-perk--${state === 'earned' ? 'active' : 'pending'}`}
          title={
            state === 'earned'
              ? 'This perk is active — folded into your entitlements.'
              : 'Earn this sigil to unlock this perk.'
          }
        >
          <span className="dw-sigil-perk-key">grants</span>
          <span className="dw-sigil-perk-val">{sigil.perk.label}</span>
        </p>
      )}
      <div className="dw-sigil-card-foot">
        <span className="dw-sigil-card-rarity">{sigil.rarity}</span>
        {sigil.cross_app && <span className="dw-sigil-card-tag">cross-app</span>}
      </div>
    </div>
  )
}
